import { Button, IconButton } from '../primitives/button.js';
import { Input } from '../primitives/input.js';
import { Delete02Icon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';

export interface StringListEditorProps {
  /** Accessible name of the list, e.g. "Argumentos de venta". */
  label: string;
  value: readonly string[];
  onChange: (value: string[]) => void;
  max?: number;
  maxLength?: number;
  placeholder?: string;
  addLabel?: string;
  disabled?: boolean;
  /** Field errors keyed by index ("0", "1"…). */
  errors?: Record<string, string>;
  id?: string;
}

/**
 * Editable list of short strings (selling points, permitted claims, allowlisted numbers).
 */
export function StringListEditor({
  label,
  value,
  onChange,
  max = 30,
  maxLength = 300,
  placeholder,
  addLabel = 'Agregar',
  disabled,
  errors,
  id,
}: StringListEditorProps) {
  const lastRef = React.useRef<HTMLInputElement | null>(null);
  const [focusLast, setFocusLast] = React.useState(false);
  React.useEffect(() => {
    if (focusLast) {
      lastRef.current?.focus();
      setFocusLast(false);
    }
  }, [focusLast, value.length]);
  return (
    <div id={id} role="group" aria-label={label} className="grid gap-2">
      {value.length === 0 ? (
        <p className="text-meta text-text-secondary">Sin elementos todavía.</p>
      ) : null}
      <ul className="grid gap-2">
        {value.map((item, i) => (
          <li key={i} className="grid gap-1">
            <div className="flex items-center gap-2">
              <Input
                ref={i === value.length - 1 ? lastRef : undefined}
                aria-label={`${label}, elemento ${i + 1}`}
                value={item}
                maxLength={maxLength}
                placeholder={placeholder}
                disabled={disabled}
                aria-invalid={errors?.[String(i)] ? true : undefined}
                onChange={(e) =>
                  onChange(value.map((v, j) => (j === i ? e.target.value : v)))
                }
              />
              <IconButton
                label={`Quitar elemento ${i + 1} de ${label}`}
                icon={<Icon icon={Delete02Icon} aria-hidden />}
                disabled={disabled}
                onClick={() => onChange(value.filter((_, j) => j !== i))}
              />
            </div>
            {errors?.[String(i)] ? (
              <p className="text-meta font-medium text-danger">
                {errors[String(i)]}
              </p>
            ) : null}
          </li>
        ))}
      </ul>
      <div>
        <Button
          variant="secondary"
          size="sm"
          leadingIcon={<Icon icon={PlusSignIcon} aria-hidden />}
          disabled={disabled || value.length >= max}
          onClick={() => {
            onChange([...value, '']);
            setFocusLast(true);
          }}
        >
          {addLabel}
        </Button>
        {value.length >= max ? (
          <span className="ml-2 text-meta text-text-secondary">
            Máximo {max} elementos.
          </span>
        ) : null}
      </div>
    </div>
  );
}

/** Drops blank entries and trims (call before saving). */
export function cleanList(items: readonly string[]): string[] {
  return items.map((s) => s.trim()).filter(Boolean);
}
